import { useState, useEffect, useRef, useCallback } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { toast } from "sonner";
import { Briefcase, ExternalLink, Clock, Coins, Eye, Play, Send, RefreshCw, Megaphone, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import GameSounds from "../lib/sounds";
import Paginator from "../components/Paginator";

function CaptchaChallenge({ type, onSolve }) {
  const [a] = useState(Math.floor(Math.random() * 20) + 1);
  const [b] = useState(Math.floor(Math.random() * 15) + 1);
  const [answer, setAnswer] = useState("");
  const expected = String(a + b);
  return (
    <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#FFD600]/30 space-y-2" data-testid="captcha-challenge">
      <p className="text-sm text-[#FFD600] font-bold">Verify you're human</p>
      <p className="text-lg text-[#F8FAFC] font-mono-game">{a} + {b} = ?</p>
      <div className="flex gap-2">
        <Input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Answer" className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 w-24 font-mono-game" data-testid="captcha-input" />
        <button onClick={() => onSolve(answer, expected)} data-testid="captcha-submit" className="px-4 py-2 text-xs font-bold uppercase bg-[#FFD600] text-[#0B0E14] rounded-lg">Verify</button>
      </div>
    </div>
  );
}

function AdViewer({ ad, captchaEnabled, captchaType, onClaimed, onClose }) {
  const [phase, setPhase] = useState("waiting"); // waiting, timing, completed
  const [timeLeft, setTimeLeft] = useState(ad.timer_seconds);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [adOpened, setAdOpened] = useState(false);
  const intervalRef = useRef(null);
  const awayAccum = useRef(0); // continuous seconds away from platform tab

  // Open ad in new tab and start tracking
  const openAd = () => {
    window.open(ad.target_url, "_blank", "noopener,noreferrer");
    setAdOpened(true);
    setPhase("timing");
  };

  // Core timer logic: counts DOWN only when platform tab is HIDDEN (user is on ad tab)
  useEffect(() => {
    if (phase !== "timing") return;

    intervalRef.current = setInterval(() => {
      const isHidden = document.hidden; // true = user is on another tab (the ad tab)

      if (isHidden) {
        // User is away on ad tab — count time
        awayAccum.current += 1;
        // Require at least 2 continuous seconds before counting (anti-instant-switch)
        if (awayAccum.current >= 2) {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(intervalRef.current);
              setPhase("completed");
              // Notify user with sound + browser notification
              try {
                const ac = new (window.AudioContext || window.webkitAudioContext)();
                [660, 880, 1100].forEach((freq, i) => {
                  const osc = ac.createOscillator(); const g = ac.createGain();
                  osc.type = "sine"; osc.frequency.value = freq;
                  g.gain.value = 0.15; g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.5 + i * 0.15);
                  osc.connect(g); g.connect(ac.destination);
                  osc.start(ac.currentTime + i * 0.15); osc.stop(ac.currentTime + 0.5 + i * 0.15);
                });
              } catch {}
              // Browser notification (if permitted)
              if (Notification.permission === "granted") {
                new Notification("Ad Complete!", { body: "Return to CrystoBag to claim your reward", icon: "/favicon.ico" });
              } else if (Notification.permission !== "denied") {
                Notification.requestPermission();
              }
              // Change document title to flash
              document.title = "✅ CLAIM YOUR REWARD — CrystoBag";
              return 0;
            }
            return prev - 1;
          });
        }
      } else {
        // User came back to platform tab — pause timer, reset continuous counter
        awayAccum.current = 0;
      }
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [phase]);

  const handleClaim = async (captchaAnswer, captchaExpected) => {
    setClaiming(true);
    try {
      const body = {};
      if (captchaAnswer !== undefined) {
        body.captcha_answer = captchaAnswer;
        body.captcha_expected = captchaExpected;
      }
      const { data } = await API.post(`/ptc/claim/${ad.ad_id}`, body);
      toast.success(data.message);
      GameSounds.reward();
      onClaimed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Claim failed");
    } finally { setClaiming(false); }
  };

  const progress = ((ad.timer_seconds - timeLeft) / ad.timer_seconds) * 100;
  const circumference = 2 * Math.PI * 36; // ~226

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="ad-viewer">
      <div className="bg-[#131722] rounded-2xl border border-[#2A3241] w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#F8FAFC]">{ad.title}</h3>
          <button onClick={onClose} className="text-[#64748B] hover:text-[#F8FAFC] text-sm">Close</button>
        </div>
        <p className="text-sm text-[#94A3B8]">{ad.description}</p>

        {/* STEP 1: Click to open ad */}
        {!adOpened && (
          <div className="text-center py-6">
            <p className="text-sm text-[#94A3B8] mb-4">Click the button below to visit the advertiser's website. Timer starts when you're on their page.</p>
            <button onClick={openAd} data-testid="open-ad-btn"
              className="px-8 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 shadow-[0_0_20px_rgba(0,240,255,0.4)] flex items-center gap-2 mx-auto">
              <ExternalLink className="w-4 h-4" /> Visit Ad
            </button>
            <p className="text-xs text-[#FFD600] mt-3">Reward: {ad.reward_coins} coins | Timer: {ad.timer_seconds}s</p>
          </div>
        )}

        {/* STEP 2: Timer running — user must stay on ad tab */}
        {adOpened && phase === "timing" && (
          <div className="text-center py-4">
            <div className="w-24 h-24 mx-auto rounded-full border-4 flex items-center justify-center mb-4 relative transition-colors duration-300"
              style={{ borderColor: document.hidden ? "#00F0FF" : "#F59E0B" }}>
              <span className="font-mono-game text-3xl font-bold" style={{ color: document.hidden ? "#00F0FF" : "#F59E0B" }}>{timeLeft}</span>
              <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="36" fill="none" stroke="#00F0FF" strokeWidth="3"
                  strokeDasharray={`${(progress / 100) * circumference} ${circumference}`}
                  strokeLinecap="round" opacity="0.4" />
              </svg>
            </div>

            <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#2A3241]/30 mb-3">
              <div className="flex items-center justify-center gap-2 mb-1">
                <div className={`w-2.5 h-2.5 rounded-full ${document.hidden ? "bg-[#10B981] animate-pulse" : "bg-[#F59E0B]"}`} />
                <span className="text-xs font-bold" style={{ color: document.hidden ? "#10B981" : "#F59E0B" }}>
                  {document.hidden ? "COUNTING — You're on the ad page" : "PAUSED — Go back to the ad page"}
                </span>
              </div>
              <p className="text-[10px] text-[#64748B]">Timer only counts while you're viewing the advertiser's website</p>
            </div>

            <p className="text-xs text-[#FFD600]">Reward: {ad.reward_coins} coins</p>
          </div>
        )}

        {/* STEP 3: Timer complete — claim reward */}
        {phase === "completed" && !showCaptcha && (() => { if (document.title.includes("CLAIM")) document.title = "CrystoBag"; return true; })() && (
          <div className="text-center py-4">
            <Check className="w-14 h-14 text-[#10B981] mx-auto mb-3" />
            <p className="text-base font-bold text-[#10B981] mb-1">Ad viewed successfully!</p>
            <p className="text-sm text-[#94A3B8] mb-5">You spent {ad.timer_seconds}s on the advertiser's page.</p>
            <button onClick={() => captchaEnabled ? setShowCaptcha(true) : handleClaim()} disabled={claiming} data-testid="claim-reward-btn"
              className="px-8 py-3 text-sm font-bold uppercase tracking-widest bg-[#10B981] text-white rounded-lg hover:bg-[#10B981]/90 disabled:opacity-50 shadow-[0_0_15px_rgba(16,185,129,0.4)]">
              {claiming ? "Claiming..." : `Claim ${ad.reward_coins} Coins`}
            </button>
          </div>
        )}

        {/* CAPTCHA step */}
        {showCaptcha && <CaptchaChallenge type={captchaType} onSolve={handleClaim} />}
      </div>
    </div>
  );
}

export default function JobsPage() {
  const [walls, setWalls] = useState([]);
  const [activeWall, setActiveWall] = useState(null);
  const [tab, setTab] = useState("ptc");
  const [sectionToggles, setSectionToggles] = useState({ ptc_enabled: true, offerwalls_enabled: true, advertise_enabled: true });
  const [ptcAds, setPtcAds] = useState([]);
  const [ptcConfig, setPtcConfig] = useState({});
  const [viewingAd, setViewingAd] = useState(null);
  const [myAds, setMyAds] = useState([]);
  const [packages, setPackages] = useState({ view_packages: [], timer_packages: [] });
  const [ptcFees, setPtcFees] = useState({ new_campaign_fee: 13, recharge_campaign_fee: 10 });
  const [showSubmit, setShowSubmit] = useState(false);
  const [adForm, setAdForm] = useState({ title: "", description: "", target_url: "", view_package_id: "", timer_package_id: "" });
  const [editingAd, setEditingAd] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", target_url: "" });
  const [rechargeAd, setRechargeAd] = useState(null);
  const [rechargeViewPkg, setRechargeViewPkg] = useState("");
  const [loading, setLoading] = useState("");
  const lastSeenRewardRef = useRef("");
  const [ptcPage, setPtcPage] = useState(1);
  const [ptcPagination, setPtcPagination] = useState({ total: 0, total_pages: 1 });
  const [ptcSort, setPtcSort] = useState("reward");
  const PTC_PER_PAGE = 20;

  // Poll for offerwall rewards every 10s when on offerwalls tab
  useEffect(() => {
    if (tab !== "offerwalls") return;
    const poll = setInterval(async () => {
      try {
        const { data } = await API.get("/offerwalls/recent-rewards");
        if (data.rewards?.length > 0) {
          const newest = data.rewards[0];
          const rewardKey = `${newest.source}_${newest.offer_id}_${newest.created_at}`;
          if (rewardKey !== lastSeenRewardRef.current) {
            lastSeenRewardRef.current = rewardKey;
            toast.success(`Offer completed! +${newest.amount} coins from ${newest.source}`);
            GameSounds.reward();
          }
        }
      } catch {}
    }, 10000);
    return () => clearInterval(poll);
  }, [tab]);

  const loadAll = useCallback(async () => {
    try {
      const [wRes, pRes, pkgRes, togRes] = await Promise.all([
        API.get("/offerwalls/config").catch(() => ({ data: { walls: [] } })),
        API.get("/ptc/ads", { params: { page: ptcPage, per_page: PTC_PER_PAGE, sort: ptcSort } }).catch(() => ({ data: { ads: [], total: 0, total_pages: 1 } })),
        API.get("/ptc/packages").catch(() => ({ data: { view_packages: [], timer_packages: [] } })),
        API.get("/settings/sections").catch(() => ({ data: {} })),
      ]);
      setSectionToggles(togRes.data);
      setWalls(wRes.data.walls || []);
      if (wRes.data.walls?.length > 0 && !activeWall) setActiveWall(wRes.data.walls[0].id);
      setPtcAds(pRes.data.ads || []);
      setPtcPagination({ total: pRes.data.total || 0, total_pages: pRes.data.total_pages || 1 });
      setPtcConfig({ captcha_enabled: pRes.data.captcha_enabled, captcha_type: pRes.data.captcha_type });
      setPackages(pkgRes.data);
      setPtcFees({ new_campaign_fee: pkgRes.data.new_campaign_fee || 13, recharge_campaign_fee: pkgRes.data.recharge_campaign_fee || 10 });
    } catch {}
  }, [activeWall, ptcPage, ptcSort]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadMyAds = async () => {
    try { const { data } = await API.get("/ptc/my-ads"); setMyAds(data.ads || []); } catch {}
  };

  const startView = async (ad) => {
    try {
      await API.post(`/ptc/view/${ad.ad_id}`);
      setViewingAd(ad);
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot view"); }
  };

  const submitAd = async () => {
    setLoading("submit");
    try {
      const { data } = await API.post("/ptc/submit-ad", adForm);
      toast.success(data.message);
      setShowSubmit(false);
      setAdForm({ title: "", description: "", target_url: "", view_package_id: "", timer_package_id: "" });
      loadMyAds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const editAd = async (adId) => {
    setLoading(`edit-${adId}`);
    try {
      const { data } = await API.put(`/ptc/edit/${adId}`, editForm);
      toast.success(data.message);
      setEditingAd(null);
      loadMyAds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const rechargeAdViews = async (adId) => {
    if (!rechargeViewPkg) return toast.error("Select a view package");
    setLoading(`recharge-${adId}`);
    try {
      const { data } = await API.post(`/ptc/recharge/${adId}`, { view_package_id: rechargeViewPkg });
      toast.success(data.message);
      GameSounds.purchase();
      setRechargeAd(null);
      setRechargeViewPkg("");
      loadMyAds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const togglePause = async (ad) => {
    const newStatus = ad.status === "paused" ? "active" : "paused";
    try {
      await API.post(`/ptc/pause/${ad.ad_id}`, { action: newStatus });
      toast.success(newStatus === "paused" ? "Ad paused" : "Ad resumed");
      loadMyAds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteMyAd = async (ad) => {
    const hasViews = (ad.remaining_views || 0) > 0;
    const msg = hasViews
      ? `Deleting will permanently remove ${ad.remaining_views} remaining views. Are you sure?`
      : "Delete this ad?";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await API.delete(`/ptc/my-ads/${ad.ad_id}`);
      toast.success(data.message);
      loadMyAds();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const active = walls.find(w => w.id === activeWall);
  const selViewPkg = packages.view_packages.find(p => p.package_id === adForm.view_package_id);
  const selTimerPkg = packages.timer_packages.find(p => p.package_id === adForm.timer_package_id);
  const baseCost = (selViewPkg?.price || 0) * (selTimerPkg?.multiplier || 1);
  const campaignFee = round2(baseCost * ptcFees.new_campaign_fee / 100);
  const totalPrice = baseCost + campaignFee;
  const rewardPerView = selViewPkg ? round2(baseCost / selViewPkg.views) : 0;
  function round2(n) { return Math.round(n * 100) / 100; }

  return (
    <GameLayout title="Jobs & Offers">
      <div className="space-y-4" data-testid="jobs-page">
        {/* Viewer overlay */}
        {viewingAd && <AdViewer ad={viewingAd} captchaEnabled={ptcConfig.captcha_enabled} captchaType={ptcConfig.captcha_type} onClaimed={() => { setViewingAd(null); loadAll(); }} onClose={() => setViewingAd(null)} />}

        {/* Tabs */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {[["ptc", "PTC Ads"], ["offerwalls", "Offerwalls"], ["advertise", "Advertise"]]
            .filter(([key]) => {
              if (key === "ptc" && !sectionToggles.ptc_enabled) return false;
              if (key === "offerwalls" && !sectionToggles.offerwalls_enabled) return false;
              if (key === "advertise" && !sectionToggles.advertise_enabled) return false;
              return true;
            })
            .map(([key, label]) => (
            <button key={key} onClick={() => { setTab(key); if (key === "advertise") loadMyAds(); }} data-testid={`jobs-tab-${key}`}
              className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === key ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── PTC ADS TAB ── */}
        {tab === "ptc" && (
          <div className="space-y-3">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-[#00F0FF]" />
                <div><p className="text-sm font-bold text-[#F8FAFC]">PTC Ads</p><p className="text-xs text-[#94A3B8]">Watch ads, earn coins. Timer must complete.</p></div>
              </div>
              <div className="flex items-center gap-3">
                <select value={ptcSort} onChange={(e) => { setPtcSort(e.target.value); setPtcPage(1); }} data-testid="ptc-sort-select"
                  className="bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-2 py-1 text-xs">
                  <option value="reward">Highest Reward</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
                <span className="text-xs text-[#64748B]">{ptcPagination.total} available</span>
              </div>
            </div>

            {ptcAds.length > 0 ? ptcAds.map(ad => (
              <div key={ad.ad_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center gap-4 hover:border-[#00F0FF]/20 transition-all" data-testid={`ptc-ad-${ad.ad_id}`}>
                <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                  <Play className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-bold text-[#F8FAFC]">{ad.title}</h4>
                    {ad.is_new && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#10B981]/15 text-[#10B981] font-bold tracking-wider" data-testid={`ad-new-badge-${ad.ad_id}`}>NEW</span>}
                  </div>
                  <p className="text-xs text-[#94A3B8] truncate">{ad.description}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-[#00F0FF] font-mono-game flex items-center gap-1"><Clock className="w-3 h-3" /> {ad.timer_seconds}s</span>
                    <span className="text-[10px] text-[#FFD600] font-mono-game flex items-center gap-1"><Coins className="w-3 h-3" /> +{ad.reward_coins}</span>
                  </div>
                </div>
                <button onClick={() => startView(ad)} data-testid={`view-ad-${ad.ad_id}`}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-lg hover:bg-[#00F0FF]/90 shadow-[0_0_10px_rgba(0,240,255,0.3)]">
                  View Ad
                </button>
              </div>
            )) : (
              <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-12 text-center">
                <Eye className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#94A3B8]">No PTC ads available right now</p>
                <p className="text-xs text-[#64748B] mt-1">Check back later or view offerwalls</p>
              </div>
            )}
            <Paginator page={ptcPage} totalPages={ptcPagination.total_pages} total={ptcPagination.total} onPageChange={setPtcPage} testIdPrefix="ptc-pager" />
          </div>
        )}

        {/* ── OFFERWALLS TAB ── */}
        {tab === "offerwalls" && (
          <div className="space-y-3">
            {walls.length > 0 ? (<>
              <div className="flex gap-1 bg-[#0B0E14] rounded-lg p-1 border border-[#2A3241]/30">
                {walls.map(w => (
                  <button key={w.id} onClick={() => setActiveWall(w.id)} className={`flex-1 py-2 text-xs font-bold uppercase rounded-md ${activeWall === w.id ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>{w.name}</button>
                ))}
              </div>
              {active && (
                <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden">
                  <div className="p-3 border-b border-[#2A3241]/30 flex items-center justify-between">
                    <span className="text-sm font-medium text-[#F8FAFC]">{active.name}</span>
                    <a href={active.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#00F0FF] flex items-center gap-1"><ExternalLink className="w-3 h-3" /> New tab</a>
                  </div>
                  <iframe src={active.url} title={active.name} className="w-full border-0" style={{ height: "600px" }} sandbox="allow-scripts allow-same-origin allow-popups allow-forms" />
                </div>
              )}
            </>) : (
              <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-12 text-center">
                <Briefcase className="w-10 h-10 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#94A3B8]">No offerwalls configured</p>
              </div>
            )}
          </div>
        )}

        {/* ── ADVERTISE TAB ── */}
        {tab === "advertise" && (
          <div className="space-y-4">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Megaphone className="w-5 h-5 text-[#FFD600]" />
                <div><p className="text-sm font-bold text-[#F8FAFC]">Advertise Your Link</p><p className="text-xs text-[#94A3B8]">Get real views from active users. Pay with coins.</p></div>
              </div>
              <button onClick={() => setShowSubmit(!showSubmit)} data-testid="toggle-submit-ad"
                className="px-4 py-2 text-xs font-bold uppercase bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90">
                <Send className="w-3 h-3 inline mr-1" /> New Ad
              </button>
            </div>

            {/* Create Ad Form */}
            {showSubmit && (
              <div className="bg-[#131722] rounded-xl border border-[#FFD600]/30 p-5 space-y-4" data-testid="submit-ad-form">
                <h3 className="text-sm font-bold text-[#F8FAFC]">Create PTC Ad</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-[#94A3B8] text-xs">Title</Label><Input value={adForm.title} onChange={(e) => setAdForm({ ...adForm, title: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                  <div><Label className="text-[#94A3B8] text-xs">Target URL</Label><Input value={adForm.target_url} onChange={(e) => setAdForm({ ...adForm, target_url: e.target.value })} placeholder="https://..." className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                </div>
                <div><Label className="text-[#94A3B8] text-xs">Description</Label><Input value={adForm.description} onChange={(e) => setAdForm({ ...adForm, description: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label className="text-[#94A3B8] text-xs">Views Package</Label><select value={adForm.view_package_id} onChange={(e) => setAdForm({ ...adForm, view_package_id: e.target.value })} className="w-full h-9 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm"><option value="">Select...</option>{packages.view_packages.map(p => <option key={p.package_id} value={p.package_id}>{p.views.toLocaleString()} views — {p.price} coins</option>)}</select></div>
                  <div><Label className="text-[#94A3B8] text-xs">Timer</Label><select value={adForm.timer_package_id} onChange={(e) => setAdForm({ ...adForm, timer_package_id: e.target.value })} className="w-full h-9 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm"><option value="">Select...</option>{packages.timer_packages.map(p => <option key={p.package_id} value={p.package_id}>{p.label} ({p.multiplier}x)</option>)}</select></div>
                </div>
                {selViewPkg && selTimerPkg && (
                  <div className="bg-[#0B0E14] rounded-lg p-3 border border-[#FFD600]/20 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-[#94A3B8]">Views:</span><span className="font-mono-game text-[#F8FAFC]">{selViewPkg.views.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span className="text-[#94A3B8]">Timer:</span><span className="font-mono-game text-[#F8FAFC]">{selTimerPkg.label} (x{selTimerPkg.multiplier})</span></div>
                    <div className="flex justify-between"><span className="text-[#94A3B8]">Base Cost:</span><span className="font-mono-game text-[#F8FAFC]">{baseCost.toFixed(2)} coins</span></div>
                    <div className="flex justify-between"><span className="text-[#F59E0B]">Platform Fee:</span><span className="font-mono-game text-[#F59E0B]">+{campaignFee.toFixed(2)} coins</span></div>
                    <div className="flex justify-between border-t border-[#2A3241]/30 pt-1"><span className="text-[#FFD600] font-bold">Total to Pay:</span><span className="font-mono-game font-bold text-[#FFD600]">{totalPrice.toFixed(2)} coins</span></div>
                    <div className="flex justify-between"><span className="text-[#00F0FF]">Reward per view:</span><span className="font-mono-game text-[#00F0FF]">{rewardPerView.toFixed(2)} coins</span></div>
                  </div>
                )}
                <button onClick={submitAd} disabled={loading === "submit" || !adForm.view_package_id || !adForm.timer_package_id || !adForm.title || !adForm.target_url} data-testid="submit-ad-btn"
                  className="px-5 py-2.5 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-40">
                  {loading === "submit" ? "Submitting..." : `Submit Ad (${totalPrice.toFixed(2)} coins)`}
                </button>
              </div>
            )}

            {/* ── MY ADS DASHBOARD ── */}
            <div>
              <h3 className="text-xs font-mono-game text-[#64748B] tracking-[0.15em] uppercase mb-3">Your Ads ({myAds.length})</h3>
              {myAds.length === 0 ? (
                <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-8 text-center">
                  <Megaphone className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                  <p className="text-sm text-[#94A3B8]">No ads yet. Create one above!</p>
                </div>
              ) : myAds.map(ad => {
                const viewsPct = ad.total_views > 0 ? ((ad.total_views_delivered || 0) / ad.total_views * 100) : 0;
                const STATUS_C = { active: "#10B981", pending: "#F59E0B", completed: "#64748B", rejected: "#EF4444", paused: "#3B82F6", inactive: "#64748B" };
                const sc = STATUS_C[ad.status] || "#64748B";
                const isEditing = editingAd === ad.ad_id;
                const isRecharging = rechargeAd === ad.ad_id;

                return (
                  <div key={ad.ad_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 mb-3 overflow-hidden" data-testid={`my-ad-${ad.ad_id}`}>
                    {/* Card header */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h4 className="text-sm font-bold text-[#F8FAFC]">{ad.title}</h4>
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase" style={{ backgroundColor: `${sc}15`, color: sc, border: `1px solid ${sc}30` }}>{ad.status}</span>
                          </div>
                          <p className="text-xs text-[#94A3B8] truncate mb-2">{ad.target_url}</p>
                          {/* Progress bar */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-2 bg-[#0B0E14] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${viewsPct}%`, backgroundColor: sc }} />
                            </div>
                            <span className="text-[10px] font-mono-game text-[#94A3B8] whitespace-nowrap">{ad.total_views_delivered || 0}/{ad.total_views}</span>
                          </div>
                          <p className="text-[10px] text-[#64748B] mt-1">{ad.remaining_views} views remaining | {ad.timer_seconds}s timer | {ad.reward_coins}c/view</p>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        <button onClick={() => { setEditingAd(isEditing ? null : ad.ad_id); setEditForm({ title: ad.title, description: ad.description || "", target_url: ad.target_url }); }}
                          className="px-3 py-1.5 text-[10px] font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg hover:bg-[#00F0FF]/10">
                          {isEditing ? "Cancel" : "Edit"}
                        </button>
                        <button onClick={() => { setRechargeAd(isRecharging ? null : ad.ad_id); setRechargeViewPkg(""); }}
                          className="px-3 py-1.5 text-[10px] font-bold uppercase text-[#FFD600] border border-[#FFD600]/30 rounded-lg hover:bg-[#FFD600]/10">
                          {isRecharging ? "Cancel" : "+ Add Views"}
                        </button>
                        {(ad.status === "active" || ad.status === "paused") && (
                          <button onClick={() => togglePause(ad)}
                            className={`px-3 py-1.5 text-[10px] font-bold uppercase rounded-lg border ${ad.status === "paused" ? "text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/10" : "text-[#3B82F6] border-[#3B82F6]/30 hover:bg-[#3B82F6]/10"}`}>
                            {ad.status === "paused" ? "Resume" : "Pause"}
                          </button>
                        )}
                        <button onClick={() => deleteMyAd(ad)}
                          className="px-3 py-1.5 text-[10px] font-bold uppercase text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/10">
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Edit form (inline) */}
                    {isEditing && (
                      <div className="px-4 pb-4 pt-0 border-t border-[#00F0FF]/20 animate-slide-up">
                        <div className="space-y-3 mt-3">
                          <p className="text-[10px] text-[#F59E0B]">Editing will send your ad back to pending for admin approval.</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label className="text-[#94A3B8] text-[10px]">Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                            <div><Label className="text-[#94A3B8] text-[10px]">URL</Label><Input value={editForm.target_url} onChange={(e) => setEditForm({ ...editForm, target_url: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                          </div>
                          <div><Label className="text-[#94A3B8] text-[10px]">Description</Label><Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-8 text-sm mt-1" /></div>
                          <button onClick={() => editAd(ad.ad_id)} disabled={loading === `edit-${ad.ad_id}`}
                            className="px-4 py-2 text-xs font-bold uppercase bg-[#F59E0B] text-[#0B0E14] rounded-lg disabled:opacity-50">
                            {loading === `edit-${ad.ad_id}` ? "Saving..." : "Save (Goes to Pending)"}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Recharge form (inline) */}
                    {isRecharging && (() => {
                      const selRechargePkg = packages.view_packages.find(p => p.package_id === rechargeViewPkg);
                      const rechargeBase = selRechargePkg?.price || 0;
                      const rechargeFeeAmt = round2(rechargeBase * ptcFees.recharge_campaign_fee / 100);
                      const rechargeTotal = rechargeBase + rechargeFeeAmt;
                      return (
                      <div className="px-4 pb-4 pt-0 border-t border-[#FFD600]/20 animate-slide-up">
                        <div className="space-y-3 mt-3">
                          <p className="text-[10px] text-[#10B981]">Adding views does NOT require re-approval. Ad stays active.</p>
                          <select value={rechargeViewPkg} onChange={(e) => setRechargeViewPkg(e.target.value)}
                            className="w-full h-9 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                            <option value="">Select view package...</option>
                            {packages.view_packages.map(p => <option key={p.package_id} value={p.package_id}>{p.views.toLocaleString()} views — {p.price} coins</option>)}
                          </select>
                          {selRechargePkg && (
                            <div className="bg-[#0B0E14] rounded-lg p-2 border border-[#FFD600]/20 text-xs space-y-1">
                              <div className="flex justify-between"><span className="text-[#94A3B8]">Base:</span><span className="font-mono-game text-[#F8FAFC]">{rechargeBase.toFixed(2)} coins</span></div>
                              <div className="flex justify-between"><span className="text-[#F59E0B]">Platform Fee:</span><span className="font-mono-game text-[#F59E0B]">+{rechargeFeeAmt.toFixed(2)} coins</span></div>
                              <div className="flex justify-between border-t border-[#2A3241]/30 pt-1"><span className="text-[#FFD600] font-bold">Total:</span><span className="font-mono-game font-bold text-[#FFD600]">{rechargeTotal.toFixed(2)} coins</span></div>
                            </div>
                          )}
                          <button onClick={() => rechargeAdViews(ad.ad_id)} disabled={loading === `recharge-${ad.ad_id}` || !rechargeViewPkg}
                            className="px-4 py-2 text-xs font-bold uppercase bg-[#FFD600] text-[#0B0E14] rounded-lg disabled:opacity-50">
                            {loading === `recharge-${ad.ad_id}` ? "Processing..." : selRechargePkg ? `Recharge (${rechargeTotal.toFixed(2)} coins)` : "Recharge Now"}
                          </button>
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
}
